import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {Avatar, Space, Tooltip} from 'antd';
import {ChevronDownIcon} from '../../icons';
import {
  StyledNavigationBottomNavigation,
  StyledNavigationCompanyDetails,
  StyledNavigationCompanyName,
  StyledNavigationDropdownContainer,
  StyledNavigationDropdown,
  StyledNavigationMenu,
  StyledNavigationMenuSpin,
  StyledNavigation
} from './StyledNavigation';
import DropdownNavigation from './DropdownNavigation';
import {useOnClickOutside} from '../../hooks';
import './Navigation.css';
import {mainActions} from '../../state/actions/main.actions';
import {KYCModalModeTypes} from '../../constants';
import {ReactComponent as WithLessIconLogo} from '../../static/images/withless-icon-logo.svg';
import {ReactComponent as WithLessLogo} from '../../static/images/withless-logo.svg';
import {elementHelpers, textHelpers} from '../../utils/helpers';

const Navigation = ({
  company,
  items = [],
  isOpenMenu,
  loading = false,
  navigationProps = {},
  openKycWindow,
  ...rest
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [collapsed, setCollapsed] = useState(true);
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);

  const menuItems = useMemo(() => {
    const dividedKeys = [...new Set(items.map(i => i.dividedKey).filter(i => Boolean(i)))];
    const undividedItems = items.filter(i => !dividedKeys.includes(i.dividedKey));
    let menuItems = [...undividedItems];

    if (Boolean(dividedKeys)) {
      dividedKeys.forEach(key => {
        const itemsByKey = items.filter(i => i.dividedKey === key);
        if (itemsByKey.length) {
          menuItems = [
            ...menuItems,
            {type: 'divider'},
            ...itemsByKey
          ];
        }
      })
    }

    return menuItems.map(({dividedKey, children, ...restItem}) => ({
      ...restItem,
      ...(children ? { children: children.map(({dividedKey, ...childRest}) => childRest) } : {})
    }));
  }, [items]);

  const collapsedClassName = useMemo(() => collapsed ? 'collapsed' : '', [collapsed]);

  useEffect(() => {
    if (isOpenMenu === collapsed) setCollapsed(!isOpenMenu);
  }, [isOpenMenu]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    elementHelpers.updateMenuItems({location, items, selectedKeys, setSelectedKeys});
  }, [location, items]); // eslint-disable-line react-hooks/exhaustive-deps

  const onCompanyChange = () => {
    navigate('/');
  }

  const onDropdownNavigationClick = () => {
    setIsOpenDropdown(false);
  }

  const handleMenuClick = (e) => {
    const { key } = e;
    let foundPath = null;
    let selectedKeyPath = [];

    for (let parent of items) {
      if (parent.key === key) {
        foundPath = parent.path;
        selectedKeyPath = [parent.key];
        break;
      }

      if (Array.isArray(parent.children)) {
        const child = parent.children.find(c => c.key === key);
        if (child) {
          foundPath = child.path;
          selectedKeyPath = [parent.key, child.key];
          break;
        }
      }
    }

    if (selectedKeyPath.length === 1 && openKeys.length > 0) {
      setOpenKeys([]);
    }

    if (foundPath) navigate(foundPath);
    setSelectedKeys(selectedKeyPath);
  };


  const onClickOutside = (e) => {
    const parentSelector = '.ant-dropdown';
    if (!e.target.closest(parentSelector)) setIsOpenDropdown(false);
  }

  const onAddCompany = () => {
    onDropdownNavigationClick();
    openKycWindow({mode: KYCModalModeTypes.COMPANY});
  }

  const getCompanyDetails = (avatarSize) => {
    const name = company?.name || '';
    const truncatedName = textHelpers.cutString(name, 35);
    const showTooltip = truncatedName.length < name.length && isOpenMenu;

    return (
      <StyledNavigationCompanyDetails>
        <Avatar
          src={company?.logo}
          size={avatarSize}
        >
          {textHelpers.getInitials(name)}
        </Avatar>
        <StyledNavigationCompanyName truncated={showTooltip}>
          {showTooltip ? (
            <Tooltip title={name}>{truncatedName}</Tooltip>
          ) : truncatedName}
        </StyledNavigationCompanyName>
      </StyledNavigationCompanyDetails>
    )
  };

  const handleOnOpenChange = (keys) => {
    const key = keys?.[0];
    const firstChild = menuItems.find(c => c.key === key)?.children?.[0];
    if (firstChild) handleMenuClick(firstChild);
    setOpenKeys(keys);
  }

  return (
    <StyledNavigation {...rest}>
      <div>
        <StyledNavigationDropdownContainer
          className={collapsedClassName}
          ref={useOnClickOutside(onClickOutside, isOpenDropdown)}
        >
          <StyledNavigationDropdown
            className={collapsedClassName}
            classNames={{root: 'navigation-dropdown-overlay'}}
            disabled={collapsed}
            popupRender={() => (
              <DropdownNavigation
                enabledCompanyCreation={false}
                onCompanyChange={onCompanyChange}
                onMenuClick={onDropdownNavigationClick}
                handleAddCompany={onAddCompany}
              />
            )}
            onClick={() => setIsOpenDropdown(!isOpenDropdown)}
            open={isOpenDropdown}
            trigger={['click']}
          >
            <Space className='d-flex justify-space-between'>
              {getCompanyDetails(24)}
              <ChevronDownIcon />
            </Space>
          </StyledNavigationDropdown>
        </StyledNavigationDropdownContainer>
        <StyledNavigationMenuSpin size='small' spinning={loading}>
          <StyledNavigationMenu
            {...navigationProps}
            inlineCollapsed={collapsed}
            items={menuItems}
            expandIcon={null}
            mode='inline'
            onClick={handleMenuClick}
            onOpenChange={handleOnOpenChange}
            selectedKeys={selectedKeys}
            openKeys={openKeys}
          />
        </StyledNavigationMenuSpin>
      </div>
      <StyledNavigationBottomNavigation>
        <Link to='/' className='d-inline-flex'>
          {collapsed ? <WithLessIconLogo /> : <WithLessLogo />}
        </Link>
      </StyledNavigationBottomNavigation>
    </StyledNavigation>
  );
}

Navigation.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      disable: PropTypes.bool,
      key: PropTypes.any.isRequired,
      icon: PropTypes.object,
      isUnread: PropTypes.bool,
      label: PropTypes.string.isRequired,
      path: PropTypes.string || null
    })
  ).isRequired,
  loading: PropTypes.bool,
  navigationProps: PropTypes.object,
}

const mapStateToProps = state => {
  const {company, main} = state;
  const {isOpenMenu} = main;
  return {
    company,
    isOpenMenu
  }
}

const mapDispatchToProps = {
  openKycWindow: mainActions.openKycWindow
}

export default connect(mapStateToProps, mapDispatchToProps)(Navigation);
